import { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import {triggerConfetti} from "@/components/Confetti.tsx";

// KPI constants
export const KPI_NASTUPY = 8
export const KPI_PARKOVANI_NASTUPU = 10

// Period-specific state interface
interface PeriodState {
  isKpiMet: boolean
  margin: number
  nastupy: number
  contracts: number
  showAnimation: boolean
  showCelebration: boolean
  currentPeriodTotal: number
}

// Context interface - All the functions and states exposed to components
// what functions and states are available to components through the context
interface CalculatorContextType {
  // States
  margin: number
  nastupy: number
  contracts: number
  showAnimation: boolean
  showCelebration: boolean
  isKpiMet: boolean
  mdsPerMonth: number
  currentPeriod: number

  // Period Management
  setCurrentPeriod: (period: number) => void
  getAllPeriods: () => PeriodState[]
  resetCurrentPeriod: () => void
  resetAllPeriods: () => void

  // Setters
  setMargin: (value: number) => void
  setNastupy: (value: number) => void
  setContracts: (value: number) => void
  setShowAnimation: (value: boolean) => void
  setShowCelebration: (value: boolean) => void
  setMdsPerMonth: (value: number) => void
  setCurrentPeriodTotal: (value: number) => void

  // Calculations
  calculate1MarginProfit: (customMargin: number, extraBonus: number, customNastupy: number, discount?: number) => number
  calculate3ContractProfit: () => number
  calculateYearlyGrossMargin: (customMargin: number) => number
  calculateTotalBusinessVolume: () => number
  getMarginPercentage: (value: number, discount?: number) => number
  getNastupyPercentage: (value: number) => number
  getContractsBonus: (contracts: number) => number

  // CSS
  getCardAnimation: (columnNumber: 2 | 3 | 4) => string
  getContractsColor: (contract: number) => string
  getNastupyColor: (nastupy: number) => string

  // Set KPI
  setKpi: () => void

  // Get total nastupy from all previous periods
  getTotalPreviousNastupy: () => number
}

const CalculatorContext = createContext<CalculatorContextType | undefined>(undefined)

const DEFAULT_PERIOD_STATE: PeriodState = {
  isKpiMet: false,
  margin: 2200,
  nastupy: 0,
  contracts: 0,
  showAnimation: false,
  showCelebration: false,
  currentPeriodTotal: 0,
}

export function CalculatorProvider({ 
  children,
}: {
  children: ReactNode
}) {
  const [currentPeriod, setCurrentPeriod] = useState(0)
  const [mdsPerMonth, setMdsPerMonth] = useState(18)
  const [periods, setPeriods] = useState<PeriodState[]>(
    Array(6).fill(DEFAULT_PERIOD_STATE)
  )

  // Get current period state
  const currentState = periods[currentPeriod]

  // Expose current period values as top-level state
  const margin = currentState.margin
  const nastupy = currentState.nastupy
  const contracts = currentState.contracts
  const showAnimation = currentState.showAnimation
  const showCelebration = currentState.showCelebration
  const isKpiMet = nastupy >= KPI_NASTUPY && contracts >= 1

  // Period-specific setters
  const updateKpiStatusForPeriod = (period: PeriodState): PeriodState => {
    return {
      ...period,
      isKpiMet: period.nastupy >= KPI_NASTUPY && period.contracts >= 1
    }
  }

  const setMargin = (value: number) => {
    console.log('setMargin() called margin=', value, 'in period=', currentPeriod)
    setPeriods(prev => prev.map((period, idx) => {
      if (idx === currentPeriod) {
        const updatedPeriod = { ...period, margin: value }
        return updateKpiStatusForPeriod(updatedPeriod)
      }
      return period
    }))
  }

  const setCurrentPeriodTotal = (value: number) => {
    console.log('setCurrentPeriodTotal() called value=', value, 'in period=', currentPeriod)
    setPeriods(prev => prev.map((period, idx) =>
        idx === currentPeriod ? {...period, currentPeriodTotal: value} : period
    ))
  }

  const setNastupy = (value: number) => {
    console.log('setNastupy() called nastupy=', value, 'in period=', currentPeriod)
    setPeriods(prev => prev.map((period, idx) => {
      if (idx === currentPeriod) {
        const updatedPeriod = { ...period, nastupy: value }
        return updateKpiStatusForPeriod(updatedPeriod)
      }
      return period
    }))
  }

  const setContracts = (value: number) => {
    console.log('setContracts() called contracts=', value, 'in period=', currentPeriod)
    setPeriods(prev => prev.map((period, idx) => {
      if (idx === currentPeriod) {
        const updatedPeriod = { ...period, contracts: value }
        return updateKpiStatusForPeriod(updatedPeriod)
      }
      return period
    }))
  }

  const setShowAnimation = (value: boolean) => {
    setPeriods(prev => prev.map((period, idx) =>
      idx === currentPeriod ? { ...period, showAnimation: value } : period
    ))
  }

  const setShowCelebration = (value: boolean) => {
    setPeriods(prev => prev.map((period, idx) =>
      idx === currentPeriod ? { ...period, showCelebration: value } : period
    ))
  }

  // Keep existing useEffect for animations
  useEffect(() => {
    if (isKpiMet) {
      setShowAnimation(true)
      triggerConfetti()
      const timer = setTimeout(() => {
        setShowAnimation(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [isKpiMet])

  const getMarginPercentage = (value: number, discount: number = 1): number => {
    if (value <= 1999) return 3.0 * discount
    if (value <= 2500) return 4.0 * discount
    if (value <= 2999) return 5.0 * discount
    if (value <= 3500) return 6.0 * discount
    return 7.0 * discount
  }

  const getNastupyPercentage = (value: number): number => {
    if (value < 8) return 0.00
    if (value === 8) return 3.00
    if (value === 9) return 3.25
    if (value === 10) return 3.50
    if (value === 11) return 3.75
    if (value >= 12) return 4.00
    return 0.00
  }

  const getContractsBonus = (contracts: number): number => {
    if (contracts === 0) return 0
    if (contracts === 1) return 15000
    if (contracts === 2) return 20000
    if (contracts === 3) return 25000
    return 30000
  }

  const calculateYearlyGrossMargin = (customMargin: number): number => {
    return mdsPerMonth * 12 * customMargin
  }

  const calculateTotalBusinessVolume = (): number => {
    return nastupy * calculateYearlyGrossMargin(margin)
  }

  const calculate1MarginProfit = (customMargin: number, extraBonus: number, customNastupy: number, discount: number = 1): number => {
    if (customMargin === 0 || customNastupy === 0) return 0
    const yearlyMargin = calculateYearlyGrossMargin(customMargin)
    const marginPercentage = (getMarginPercentage(customMargin, discount) / 100) + (Number(extraBonus) / 100)
    return Math.round(customNastupy * yearlyMargin * marginPercentage / 2) // /2 because of 6 months ... 1/2 year
  }

  const calculate3ContractProfit = (): number => {
    if (nastupy < KPI_NASTUPY) return 0
    return contracts * getContractsBonus(contracts)
  }

  const getNastupyColor = (nastupy: number) => {
    if (nastupy >= KPI_NASTUPY) return "border-green-500 bg-green-50"
    if (nastupy >= 3) return "border-orange-500 bg-orange-50"
    return "border-red-500 bg-red-50"
  }

  const getContractsColor = (contracts: number) => {
    if (contracts >= 1) return "border-green-500 bg-green-50"
    if (contracts >= 0) return "border-red-500 bg-red-50"
    return "border-red-500 bg-red-50"
  }

  const getCardAnimation = (columnNumber: 2 | 3 | 4) => {
    if (!showAnimation || nastupy < KPI_NASTUPY) return ""

    // Stagger the animations slightly
    const delay = (columnNumber - 2) * 0.2 // 0s, 0.2s, 0.4s delay
    return `animate-bounce-once [animation-delay:${delay}s]`
  }

  const resetCurrentPeriod = () => {
    setPeriods(prev => prev.map((period, idx) =>
      idx === currentPeriod ? DEFAULT_PERIOD_STATE : period
    ))
  }

  const resetAllPeriods = () => {
    setPeriods(Array(6).fill(DEFAULT_PERIOD_STATE))
  }

  const setKpi = () => {
    setPeriods(prev => prev.map((period, idx) =>
      idx === currentPeriod ? updateKpiStatusForPeriod(period) : period
    ))
  }

  const getTotalPreviousNastupy = () => {
    return periods.slice(0, currentPeriod).reduce((total, period) => total + period.nastupy, 0)
  }

  const value = {
    // States
    margin,
    nastupy,
    contracts,
    showAnimation,
    showCelebration,
    isKpiMet,
    mdsPerMonth,
    currentPeriod,

    // Period Management
    setCurrentPeriod,
    getAllPeriods: () => periods,
    resetCurrentPeriod,
    resetAllPeriods,

    // Setters
    setMargin,
    setNastupy: setNastupy,
    setContracts,
    setShowAnimation,
    setShowCelebration,
    setMdsPerMonth,
    setCurrentPeriodTotal,

    // Calculations
    calculate1MarginProfit: calculate1MarginProfit,
    calculate3ContractProfit: calculate3ContractProfit,
    calculateYearlyGrossMargin,
    calculateTotalBusinessVolume,
    getMarginPercentage,
    getNastupyPercentage,
    getContractsBonus,

    // CSS
    getCardAnimation,
    getContractsColor,
    getNastupyColor,

    // Set KPI
    setKpi,

    // Get total nastupy from all previous periods
    getTotalPreviousNastupy: getTotalPreviousNastupy,
  }

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  )
}

export function useCalculator() {
  const context = useContext(CalculatorContext)
  if (context === undefined) {
    throw new Error('useCalculator must be used within a CalculatorProvider')
  }
  return context
} 